import type { ListenerParameters } from 'pubnub'
import { v4 } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { getProperTimetoken } from 'v2/pages/conversations/utils'
import { toChunkModel } from 'v2/pages/conversations/utils/transformers'
import { type Chunk, ChunkType, ChunkState, type ChunkSender } from 'v2/types/chunk.internal'
import { ChunkOrigin, type ChatMessagePayload, type FileMessagePayload } from 'v2/types/chunk.external'
import { ConvChannels, type RActiveConversation, type RNewConversation } from 'v2/types/api/conversations'
import { type PubNubRef, PubnubEvents } from 'v2/types/pubnub'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { pubnubLogger } from 'v2/utils/logger'
import { getLocalAuth } from 'v2/utils'
import { useSharedConversationStore } from 'v2/state/conversation'
import { isEmpty } from 'lodash'
import {
  addChunkToQueryCache,
  updateChunkInQueryCache,
} from 'v2/pages/conversations/mutations/conversations'
import { getSettingsQueryKeys } from './query-key-management'

export async function setLastReadTts() {
  try {
    const tts = useSharedConversationStore.getState().lastReadTimestamp
    const details = getLocalAuth()
    if (isEmpty(tts) || !details.aom_uuid) return
    const memberships = await window.cx.pubnubInstance?.objects.setMemberships({
      channels: [
        {
          id: `aom.${details.aom_uuid}`,
          custom: tts,
        },
      ],
    })
    pubnubLogger.log('setting last read tts', memberships)
    return memberships
  } catch (error) {
    pubnubLogger.error('Error setting memberships', error)
  }
  return null
}

export const hasActiveChatConversation = (conversation: RActiveConversation) =>
  conversation.child_conversations.some(
    (child) => child.channel === ConvChannels.CHAT && !child.end_at,
  )

export function addPubNubEventListeners(
  listenerRef: React.MutableRefObject<ListenerParameters | null>,
  pubnubRef: React.MutableRefObject<PubNubRef | null>,
) {
  pubnubLogger.debug('Adding PubNub listeners')
  const listeners: ListenerParameters = {
    message: (m) => {
      try {
        pubnubLogger.debug('original message', m)
        if (m.message) {
          const { message } = m
          if (m.message.content.sender) {
            pubnubEventBus.emit(
              PubnubEvents.CHAT_MESSAGE,
              toChunkModel(m, ChunkOrigin.pubnubChatEvent),
            )
            return
          }
          pubnubEventBus.emit(message.content.eventName, message.content.eventData)
        }
      } catch (error) {
        pubnubLogger.error('Error processing message', error)
      }
    },
    signal: (signal) => {
      pubnubLogger.debug('signal', signal)
    },
    messageAction: (messageAction) => {
      pubnubEventBus.emit(PubnubEvents.MESSAGE_ACTION, messageAction)
    },
    file: (file) => {
      pubnubLogger.debug('file', file)
    },
    status: (status) => {
      pubnubLogger.debug('status', status)
      if (status.statusCode === 403 || status.category === 'PNTimeoutCategory') {
        queryClient.invalidateQueries(
          { queryKey: getSettingsQueryKeys().pubnubTokenBase },
          { cancelRefetch: false },
        )
      }
    },
  }

  listenerRef.current = listeners
  pubnubRef.current?.pubnub.addListener(listeners)
}

type PubNubAgentMessagePayload = ChatMessagePayload | FileMessagePayload

const createChatMessageChunk = (
  payload: PubNubAgentMessagePayload,
  conversation: RActiveConversation | RNewConversation,
): Chunk<ChunkType.chatMessage | ChunkType.file> => {
  const isFile = payload.type === ChunkType.file
  const baseChunk = {
    id: payload.tmpId || v4(),
    timetoken: getProperTimetoken(Date.now()),
    type: payload.type,
    sender: payload.content.sender as ChunkSender,
    to: 'customer',
    channel: ConvChannels.CHAT,
    contactUuid: conversation.contact_uuid,
    meta: {
      sender: payload.content.sender,
      state: ChunkState.PENDING,
    },
  }

  if (isFile) {
    const filePayload = payload as FileMessagePayload
    return {
      ...baseChunk,
      label: `File: ${filePayload.content.message[0].file_name}`,
      meta: {
        ...baseChunk.meta,
        fileData: filePayload.content.message,
        senderId: '',
      },
    }
  }
  const chatPayload = payload as ChatMessagePayload
  return {
    ...baseChunk,
    label: chatPayload.content.message,
    meta: {
      ...baseChunk.meta,
      message: chatPayload.content.message,
    },
  }
}

export const addChatMessageChunkToCache = (
  payload: PubNubAgentMessagePayload,
  conversation: RActiveConversation | RNewConversation,
) => {
  const chunk = createChatMessageChunk(payload, conversation)
  addChunkToQueryCache(chunk, conversation.conversation_number)
}

export async function publishAndUpdateCache(
  contactUUID: string,
  conversationNumber: string,
  payload: PubNubAgentMessagePayload,
  isRetry = false,
) {
  try {
    // Feature: Unread count management
    useSharedConversationStore
      .getState()
      .setLastReadTimestamp(conversationNumber, Date.now())
    if (isRetry) {
      // For retry, update the existing chunk state to PENDING
      updateChunkInQueryCache(
        conversationNumber,
        (c) => c.id === payload.tmpId,
        (c) => ({
          meta: {
            ...c.meta,
            state: ChunkState.PENDING,
            stateReason: 'Retrying to publish message, please wait...',
          },
        }),
      )
    } else {
      // For initial publish, add the chunk to cache
      addChatMessageChunkToCache(payload, {
        conversation_number: conversationNumber,
        contact_uuid: contactUUID,
      } as RActiveConversation)
    }

    await window.cx.pubnub.publishMutation.mutateAsync({
      channel: contactUUID,
      message: payload,
    })

    pubnubLogger.debug('Published message for chunk:', payload.tmpId)
  } catch (error) {
    updateChunkInQueryCache(
      conversationNumber,
      (c) => c.id === payload.tmpId,
      (c) => ({
        meta: {
          ...c.meta,
          state: ChunkState.ERROR,
          stateReason: 'Error publishing message, please try again.',
        },
      }),
    )
    pubnubLogger.error('Error publishing message', error)
    throw error
  }
}
